import React, { useEffect } from "react";
import { Modal, Form, Input, Button, Row, Col, Checkbox } from "antd";
import { Contact } from "../../types";
import { ActivationStatus } from "../../types";

interface ContactModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (values: any) => void;
    editingRecord: Contact | null;
    establishmentLocationId?: string;
    placeId?: string;
    loading: boolean;
}

const ContactModal: React.FC<ContactModalProps> = ({
    visible,
    onCancel,
    onSave,
    editingRecord,
    establishmentLocationId,
    placeId,
    loading,
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible) {
            if (editingRecord) {
                form.setFieldsValue(editingRecord);
            } else {
                form.resetFields();
            }
        }
    }, [visible, editingRecord, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    return (
        <Modal
            title={editingRecord ? "Edit Contact" : "Add Contact"}
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>
                    {editingRecord ? "Update" : "Create"}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" initialValues={{ status: ActivationStatus.Active, isPrimary: false }}>
                <Form.Item name="id" hidden>
                    <Input />
                </Form.Item>

                <Form.Item name="establishmentLocationId" hidden initialValue={establishmentLocationId}>
                    <Input />
                </Form.Item>

                <Form.Item name="placeId" hidden initialValue={placeId}>
                    <Input />
                </Form.Item>

                <Form.Item name="status" hidden initialValue={ActivationStatus.Active}>
                    <Input />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Contact Name"
                            name="name"
                            rules={[{ required: true, message: "Please enter contact name" }]}
                        >
                            <Input placeholder="e.g., John Smith" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Title/Position" name="title">
                            <Input placeholder="e.g., Lost & Found Manager" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Department" name="department">
                            <Input placeholder="e.g., Customer Service" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Email" name="email">
                            <Input type="email" placeholder="e.g., john.smith@lax.com" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label="Phone" name="phone">
                            <Input placeholder="e.g., +1-310-646-5252" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="Mobile Phone" name="mobilePhone">
                            <Input placeholder="e.g., +1-310-555-0123" />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="Fax" name="fax">
                            <Input placeholder="e.g., +1-310-646-5253" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Street Address" name="address">
                            <Input placeholder="e.g., 1 World Way, Room 201" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="City" name="city">
                            <Input placeholder="e.g., Los Angeles" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="State/Province" name="state">
                            <Input placeholder="e.g., California" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Country" name="country">
                            <Input placeholder="e.g., United States" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="ZIP/Postal Code" name="zipCode">
                            <Input placeholder="e.g., 90045" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Website" name="website">
                            <Input type="url" placeholder="e.g., https://www.laxairport.com" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Lost & Found Form Link" name="lostAndFoundFormLink">
                            <Input type="url" placeholder="e.g., https://www.laxairport.com/lost-and-found" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item label="Notes" name="notes">
                            <Input.TextArea
                                rows={3}
                                placeholder="e.g., Available Mon-Fri 8AM-5PM, Contact for urgent matters"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="isPrimary" valuePropName="checked">
                            <Checkbox>Set as Primary Contact</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ContactModal;
